(ns app.front.view.group.admin.ai.rules
  (:require [app.front.ui :as ui]
            [app.front.ui.multi-select :as ui-ms]
            [app.front.ui.radio :as ui-radio]
            [app.front.ui.select :as ui-select]
            [app.front.ui.sortable-table :as sortable-table]
            [app.front.util :as futil]
            [app.front.view.group.admin.ai.files :as files]
            [app.front.view.group.admin.common :as acommon]
            [app.front.view.group.admin.subs :as gas]
            [clojure.string :as str]
            [core.front.carbon :as cc]
            [core.front.event :refer [>evt <sub]]
            [core.front.icons :as i]
            [potpuri.core :as p]
            [re-frame.core-instrumented :as rf]))

(def default-rule-form
  {:rule-text       ""
   :source-text     ""
   :source-citation ""
   :evidence-hint   ""
   :page-start      ""
   :page-end        ""
   :product-ids     #{}
   :status          :active
   :needs-review    true})

(rf/reg-event-fx ::list-rules
  (fn [_ [_ group-id]]
    {:comm/command {:command    :group/list-ai-rules
                    :method     :get
                    :data       {:group-id group-id}
                    :on-success [::set-rules]}}))

(rf/reg-event-db ::set-rules
  (fn [db [_ _ rules]]
    (assoc db ::rules rules)))

(rf/reg-event-db ::new-rule
  (fn [db _]
    (assoc db ::rule-form default-rule-form)))

(defn- rule->form [rule]
  (-> rule
      (select-keys [:id :rule-text :file-id :source-text :source-citation :page-start :page-end :evidence-hint
                    :product-ids :status :needs-review])
      (update :page-start #(if % (str %) ""))
      (update :page-end #(if % (str %) ""))
      (update :product-ids set)))

(def rule-editor-id "ai-rule-editor")

(rf/reg-event-fx ::edit-rule
  (fn [{:keys [db]} [_ rule]]
    {:db                       (assoc db ::rule-form (rule->form rule))
     ::futil/scroll-to-section rule-editor-id}))

(rf/reg-event-db ::cancel-rule-edit
  (fn [db _]
    (dissoc db ::rule-form)))

(rf/reg-event-db ::rule-form-field-changed
  (fn [db [_ field value]]
    (assoc-in db [::rule-form field] value)))

(rf/reg-event-db ::set-product-filter
  (fn [db [_ product-id]]
    (-> db
        (assoc ::product-filter product-id)
        (dissoc ::selection))))

(rf/reg-event-db ::select-rule
  (fn [db [_ selected? rule-id]]
    (update db ::selection (fnil (if selected? conj disj) #{}) rule-id)))

(rf/reg-event-db ::select-all-rules
  (fn [db [_ rule-ids]]
    (assoc db ::selection (set rule-ids))))

(rf/reg-event-db ::clear-selection
  (fn [db _]
    (dissoc db ::selection)))

(defn- optional-text [value]
  (some-> value str/trim not-empty))

(defn- optional-int [value]
  (when-let [text (optional-text value)]
    (let [number (js/parseInt text 10)]
      (when-not (js/isNaN number)
        number))))

(defn- rule-form->payload [group-id {:keys [id rule-text file-id source-text source-citation page-start page-end evidence-hint
                                            product-ids status needs-review]}]
  (p/assoc-if {:group-id     group-id
               :rule-text    (str/trim rule-text)
               :product-ids  (vec product-ids)
               :status       status
               :needs-review (boolean needs-review)}
              :id id
              :file-id (optional-text file-id)
              :source-text (optional-text source-text)
              :source-citation (optional-text source-citation)
              :page-start (optional-int page-start)
              :page-end (optional-int page-end)
              :evidence-hint (optional-text evidence-hint)))

(rf/reg-event-fx ::save-rule
  (fn [{:keys [db]} [_ group-id form]]
    (let [update? (:id form)]
      {:db           (assoc db ::saving-rule? true)
       :comm/command {:command          (if update? :group/update-ai-rule :group/create-ai-rule)
                      :method           :post
                      :data             (rule-form->payload group-id form)
                      :notify           {:success :group.ai-files/rule-save-success}
                      :on-success       [::save-rule-success group-id]
                      :finally-dispatch [::save-rule-done]}})))

(rf/reg-event-fx ::save-rule-success
  (fn [{:keys [db]} [_ group-id]]
    {:db         (dissoc db ::rule-form)
     :dispatch-n [[::list-rules group-id]]}))

(rf/reg-event-db ::save-rule-done
  (fn [db _]
    (dissoc db ::saving-rule?)))

(rf/reg-event-fx ::delete-rules
  (fn [_ [_ group-id rule-ids]]
    {:comm/command {:command    :group/delete-ai-rules
                    :method     :post
                    :data       {:group-id group-id
                                 :ids      (vec rule-ids)}
                    :notify     {:success :group.ai-files/rules-delete-success}
                    :on-success [::delete-success group-id]}}))

(rf/reg-event-fx ::bulk-update-status-selected-rules
  (fn [_ [_ group-id rule-ids status]]
    {:comm/command {:command    :group/bulk-update-ai-rule-status
                    :method     :post
                    :data       {:group-id group-id
                                 :ids      (vec rule-ids)
                                 :status   status}
                    :notify     {:success :group.ai-files/rules-status-update-success}
                    :on-success [::delete-success group-id]}}))

(rf/reg-event-fx ::delete-success
  (fn [{:keys [db]} [_ group-id]]
    {:db         (dissoc db ::selection)
     :dispatch-n [[::list-rules group-id]]}))

(rf/reg-sub ::rules :-> ::rules)
(rf/reg-sub ::rule-form :-> ::rule-form)
(rf/reg-sub ::saving-rule? :-> ::saving-rule?)
(rf/reg-sub ::product-filter :-> ::product-filter)
(rf/reg-sub ::selection :-> ::selection)

(defn- permit-type-label [tr permit-type]
  (tr [(keyword "permit-type" (name permit-type))]))

(defn- source-id [file]
  (some-> file :db/id str))

(defn- file-entity [files-by-id file-id]
  (when file-id
    (get files-by-id (futil/parse-int file-id))))

(defn- file-name [tr files-by-id file-id]
  (or (some-> (file-entity files-by-id file-id) :file/filename)
      (when file-id (tr [:group.ai-files/unavailable-source-document]))
      "—"))

(defn- source-options [files-by-id]
  (->> files-by-id vals (map source-id) sort vec))

(defn- products-text [tr product-ids]
  (if (seq product-ids)
    (->> product-ids
         (map #(permit-type-label tr %))
         sort
         (str/join ", "))
    (tr [:group.ai-files/all-products])))

(def rule-statuses
  [:active :draft :inactive])

(defn- status-text [tr rule]
  (tr [(keyword "group.ai-files" (str "status-" (name (:status rule))))]))

(defn- review-status-text [tr rule]
  (tr [(if (:needs-review rule) :group.ai-files/pending-review :group.ai-files/reviewed)]))

(defn- optional-label [tr label-key]
  (str (tr [label-key]) " (" (tr [:optional]) ")"))

(defn- rule-form-field [tr field label-key helper-key rows {:keys [optional? placeholder-key required?]}]
  (let [id    (str "ai-rule-" (name field))
        value (get (<sub [::rule-form]) field)]
    [:div {:data-guide-id                 id
           :data-guide-label              (tr [label-key])
           :data-guide-highlight-selector "textarea"}
     [ui/text-area (p/assoc-if {:id          id
                                :label-text  (if optional? (optional-label tr label-key) (tr [label-key]))
                                :label-class "font-semibold"
                                :helper-text (tr [helper-key])
                                :value       (or value "")
                                :rows        rows
                                :light       true
                                :required    (boolean required?)
                                :on-change   #(>evt [::rule-form-field-changed field (-> % .-target .-value)])}
                               :placeholder (some-> placeholder-key vector tr))]]))

(defn- rule-page-field [tr field label-key placeholder-key]
  (let [id    (str "ai-rule-" (name field))
        value (get (<sub [::rule-form]) field)]
    [:div.flex.items-center.gap-2.min-w-0
     [:label.bx--label.mb-0.whitespace-nowrap {:for id} (tr [label-key])]
     [:div.flex-1
      (cc/text-input {:id          id
                      :label-text  (tr [label-key])
                      :hide-label  true
                      :type        :number
                      :value       (or value "")
                      :placeholder (tr [placeholder-key])
                      :light       true
                      :on-change   #(>evt [::rule-form-field-changed field (-> % .-target .-value)])})]]))

(defn- rule-form [tr group-id]
  (let [{:keys [id rule-text product-ids status needs-review] :as form} (<sub [::rule-form])
        saving?                                                         (<sub [::saving-rule?])
        permit-types                                                    (<sub [::gas/group-permit-types])
        files-by-id                                                     (<sub [::files/files])]
    (when form
      [:div.m-8.p-6.bg-ui-light-gray
       {:id                     rule-editor-id
        :data-guide-id          "ai-rule-editor"
        :data-guide-label       (tr [(if id :group.ai-files/edit-rule :group.ai-files/new-rule)])
        :data-guide-description (tr [:group.ai-files/rule-editor-guide])}
       [:h3.mb-4.text-lg.font-semibold (tr [(if id :group.ai-files/edit-rule :group.ai-files/new-rule)])]
       [:div.grid.grid-cols-1.gap-4
        [rule-form-field tr :rule-text :group.ai-files/instruction :group.ai-files/instruction-help 5
         {:required? true}]
        [rule-form-field tr :evidence-hint :group.ai-files/when-apply :group.ai-files/when-apply-help 4
         {:optional? true}]
        [:div.grid.grid-cols-1.md:grid-cols-2.gap-4.items-start
         [rule-form-field tr :source-citation :group.ai-files/supporting-citation :group.ai-files/supporting-citation-help 1
          {:optional?       true
           :placeholder-key :group.ai-files/supporting-citation-placeholder}]
         [rule-form-field tr :source-text :group.ai-files/supporting-text :group.ai-files/supporting-text-help 3
          {:optional?       true
           :placeholder-key :group.ai-files/supporting-text-placeholder}]]
        [:div.grid.grid-cols-1.md:grid-cols-2.gap-4.items-start
         [:div {:data-guide-id                 "ai-rule-source-document"
                :data-guide-label              (tr [:group.ai-files/source-document])
                :data-guide-highlight-selector "select"}
          (cc/form-label {:class-name "font-semibold"} (tr [:group.ai-files/source-document]))
          [:div.bx--form__helper-text.mb-2 (tr [:group.ai-files/source-document-help])]
          [ui-select/select {:id          "ai-rule-file-id"
                             :label-text  (tr [:group.ai-files/source-document])
                             :hide-label  true
                             :select-text (tr [:group.ai-files/no-source-document])
                             :light       true
                             :values      (source-options files-by-id)
                             :value       (:file-id form)
                             :val->str    #(file-name tr files-by-id %)
                             :on-change   #(>evt [::rule-form-field-changed :file-id %])}]]
         (cc/form-group
           {:legend-text (optional-label tr :group.ai-files/document-pages)
            :class-name  "m-0 min-w-0 [&_.bx--label]:!font-semibold"}
           [:<>
            [:div.bx--form__helper-text.mb-2 (tr [:group.ai-files/document-pages-help])]
            [:div.grid.grid-cols-1.sm:grid-cols-2.gap-4
             [rule-page-field tr :page-start :group.ai-files/from-page :group.ai-files/page-start-placeholder]
             [rule-page-field tr :page-end :group.ai-files/to-page :group.ai-files/page-end-placeholder]]])]
        [:div {:data-guide-id                 "ai-rule-products"
               :data-guide-label              (tr [:group.ai-files/products])
               :data-guide-highlight-selector "button"}
         (cc/form-label {:class-name "font-semibold"} (tr [:group.ai-files/products]))
         [:div.bx--form__helper-text.mb-2 (tr [:group.ai-files/products-help])]
         [ui-ms/multi-select {:id          "ai-rule-products"
                              :label-text  (tr [:group.ai-files/products])
                              :hide-label? true
                              :light       true
                              :value       product-ids
                              :values      permit-types
                              :val->str    #(permit-type-label tr %)
                              :on-change   #(>evt [::rule-form-field-changed :product-ids %])}]]
        [:div.grid.grid-cols-1.md:grid-cols-2.gap-4.items-start
         (cc/form-group
           {:legend-text (tr [:group.ai-files/status])
            :class-name  "m-0 min-w-0 [&_.bx--label]:!font-semibold"}
           [:<>
            [:div.bx--form__helper-text.mb-2 (tr [:group.ai-files/status-help])]
            [ui-radio/radio {:name        "ai-rule-status"
                             :label-text  (tr [:group.ai-files/status])
                             :hide-label  true
                             :values      rule-statuses
                             :value       status
                             :val->str    #(tr [(keyword "group.ai-files" (str "status-" (name %)))])
                             :orientation :horizontal
                             :on-change   #(>evt [::rule-form-field-changed :status %])}]])
         [:div {:data-guide-id    "ai-rule-review-status"
                :data-guide-label (tr [:group.ai-files/review-status])}
          (cc/form-group
            {:legend-text (tr [:group.ai-files/review-status])
             :class-name  "m-0 min-w-0 [&_.bx--label]:!font-semibold"}
            [:<>
             [:div.bx--form__helper-text.mb-2 (tr [:group.ai-files/review-status-help])]
             (cc/checkbox {:id         "ai-rule-needs-review"
                           :label-text (tr [:group.ai-files/pending-review])
                           :checked    (boolean needs-review)
                           :on-change  #(>evt [::rule-form-field-changed :needs-review %])})])]]]
       [:div.buttons.mt-4
        (cc/button {:kind             :primary
                    :icon             i/check
                    :icon-description ""
                    :disabled         (or saving? (str/blank? rule-text))
                    :on-click         #(>evt [::save-rule group-id form])}
                   (tr [:save]))
        (cc/button {:kind             :tertiary
                    :icon             i/remove
                    :icon-description ""
                    :on-click         #(>evt [::cancel-rule-edit])}
                   (tr [:cancel]))]])))

(defn- detail [label value]
  [:div.mb-4
   [:div.font-semibold label]
   [:div.whitespace-pre-line.mt-2 (or (not-empty value) "—")]])

(defn- expanded-rule [tr files-by-id rule]
  [:div.grid.grid-cols-1.md:grid-cols-2.gap-x-8
   [:div
    [detail (tr [:group.ai-files/instruction]) (:rule-text rule)]
    [detail (tr [:group.ai-files/when-apply]) (:evidence-hint rule)]
    [detail (tr [:group.ai-files/source-document]) (file-name tr files-by-id (:file-id rule))]
    [detail (tr [:group.ai-files/status]) (status-text tr rule)]]
   [:div
    [detail (tr [:group.ai-files/supporting-citation]) (:source-citation rule)]
    [detail (tr [:group.ai-files/supporting-text]) (:source-text rule)]
    [detail (tr [:group.ai-files/document-pages]) (str/join " "
                                                            [(tr [:group.ai-files/from-page])
                                                             (or (:page-start rule) "—") "–"
                                                             (tr [:group.ai-files/to-page])
                                                             (or (:page-end rule) "—")])]
    [detail (tr [:group.ai-files/review-status]) (review-status-text tr rule)]]])

(defn- filtered-rules [rules product-filter]
  (if product-filter
    (filter #(or (empty? (:product-ids %))
                 (contains? (set (:product-ids %)) product-filter))
            rules)
    rules))

(defn- product-filter [tr]
  (let [permit-types (<sub [::gas/group-permit-types])
        value        (<sub [::product-filter])]
    [:div.m-8.max-w-md
     {:data-guide-id                 "ai-rule-product-filter"
      :data-guide-label              (tr [:group.ai-files/product-filter])
      :data-guide-highlight-selector "select"}
     [ui-select/select {:id          "ai-rule-product-filter"
                        :label-text  (tr [:group.ai-files/product-filter])
                        :select-text (tr [:group.ai-files/all-products])
                        :values      permit-types
                        :value       value
                        :val->str    #(permit-type-label tr %)
                        :on-change   #(>evt [::set-product-filter %])}]]))

(defn- rules-table [tr group-id]
  (let [rules          (<sub [::rules])
        product-filter (<sub [::product-filter])
        visible-rules  (vec (filtered-rules rules product-filter))
        files-by-id    (<sub [::files/files])
        selection      (<sub [::selection])]
    [:div.m-8
     {:data-guide-id    "ai-rule-table"
      :data-guide-label (tr [:group.ai-files/rule-table])}
     (if (seq visible-rules)
       [sortable-table/table
        {:cols            [[(tr [:group.ai-files/instruction]) :rule-text {:sorting true :truncate? true}]
                           [(tr [:group.ai-files/source-document]) #(file-name tr files-by-id (:file-id %)) {:sorting true}]
                           [(tr [:group.ai-files/products]) #(products-text tr (:product-ids %)) {:sorting true}]
                           [(tr [:group.ai-files/status]) #(status-text tr %) {:sorting true}]
                           [(tr [:group.ai-files/review-status]) #(review-status-text tr %) {:sorting true}]]
         :items           visible-rules
         :id-func         :id
         :selection       selection
         :on-select       #(>evt [::select-rule %1 %2])
         :on-clear        #(>evt [::clear-selection])
         :on-select-all   #(>evt [::select-all-rules (mapv :id visible-rules)])
         :toolbar-actions [{:title           (tr [:group.ai-files/activate-selected])
                            :on-click        #(>evt [::bulk-update-status-selected-rules group-id selection :active])
                            :disabled?       (empty? selection)
                            :confirm?        true
                            :confirm-content (tr [:group.ai-files/activate-selected-confirm])}
                           {:title           (tr [:group.ai-files/deactivate-selected])
                            :on-click        #(>evt [::bulk-update-status-selected-rules group-id selection :inactive])
                            :disabled?       (empty? selection)
                            :confirm?        true
                            :confirm-content (tr [:group.ai-files/deactivate-selected-confirm])}
                           {:title           (tr [:group.ai-files/delete-selected])
                            :on-click        #(>evt [::delete-rules group-id selection])
                            :disabled?       (empty? selection)
                            :confirm?        true
                            :confirm-content (tr [:group.ai-files/delete-selected-confirm])}]
         :expanded-func   #(expanded-rule tr files-by-id %)
         :make-actions    (fn [rule]
                            [{:title    (tr [:edit])
                              :on-click #(>evt [::edit-rule rule])
                              :icon     i/pen}
                             {:title           (tr [:delete])
                              :on-click        #(>evt [::delete-rules group-id [(:id rule)]])
                              :icon            i/trash
                              :confirm?        true
                              :confirm-content (tr [:group.ai-files/rule-delete-confirm])}])
         :tr              tr}]
       [:p (tr [:group.ai-files/no-rules])])]))

(defn section [tr group-id]
  [acommon/section
   {:data-guide-page-description (tr [:group.ai-files/rules-help])}
   [:div.m-8.flex.justify-between.gap-4.items-start
    [:div
     [:h2.text-2xl.font-semibold (tr [:group.ai-files/department-rules])]
     [:p (tr [:group.ai-files/rules-help])]
     [:p.mt-2 (tr [:group.ai-files/precheck-rules-help])]]
    (cc/button {:kind             :primary
                :icon             i/plus
                :icon-description (tr [:group.ai-files/add-rule])
                :on-click         #(>evt [::new-rule])}
               (tr [:group.ai-files/add-rule]))]
   [rule-form tr group-id]
   [product-filter tr]
   [rules-table tr group-id]])
